/**
 * Fornitori — anagrafica fornitori raccolta automaticamente dalle bolle
 * con storico bolle collegate. Solo admin e impiegati amministrativi.
 */
import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Building2, Plus, Search, Trash2, Edit3, X, Receipt,
  MapPin, Phone, Mail, Landmark, TrendingUp, ChevronRight, FileSpreadsheet, Save,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api from "@/api";
import FornitoriCostiRating from "./FornitoriCostiRating";
import useFormDraft from "@/hooks/useFormDraft";
const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const CATEGORIE = [
  "Materiali",
  "Materiali edili",
  "Elettrici",
  "Idrici",
  "Condizionamento",
  "Porte",
  "Serramenti",
  "Impianti",
  "Finiture",
  "Ferramenta",
  "Attrezzature",
  "Trasporti",
  "Servizi",
  "Altro",
];
const EMPTY_FORM = {
  ragione_sociale: "", partita_iva: "", categoria: "Materiali",
  indirizzo: "", email: "", telefono: "",
  condizioni_pagamento: "30 gg data fattura", iban: "", note: "",
};

export default function Fornitori() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState("rubrica");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // fornitore in edit oppure "new"
  const [historyFor, setHistoryFor] = useState(null); // apre modale storico bolle

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["fornitori-stats"],
    queryFn: () => api.get("/entita/fornitori/stats").then((r) => r.data),
  });

  const saveMut = useMutation({
    mutationFn: (payload) => {
      if (editing?.fornitore_id) {
        return api.patch(`/entita/fornitori/${editing.fornitore_id}`, payload).then((r) => r.data);
      }
      return api.post("/entita/fornitori", payload).then((r) => r.data);
    },
    onSuccess: () => {
      toast.success(editing?.fornitore_id ? "Fornitore aggiornato" : "Fornitore aggiunto");
      qc.invalidateQueries({ queryKey: ["fornitori-stats"] });
      setEditing(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const delMut = useMutation({
    mutationFn: (fid) => api.delete(`/entita/fornitori/${fid}`),
    onSuccess: () => {
      toast.success("Fornitore eliminato");
      qc.invalidateQueries({ queryKey: ["fornitori-stats"] });
    },
  });

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(
      (f) => (f.ragione_sociale || "").toLowerCase().includes(s)
        || (f.partita_iva || "").toLowerCase().includes(s)
        || (f.indirizzo || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const kpi = useMemo(() => ({
    tot: items.length,
    con_anagrafica: items.filter((i) => i.fornitore_id).length,
    orfani: items.filter((i) => i.solo_bolle).length,
    spesa: items.reduce((s, i) => s + Number(i.spesa_totale || 0), 0),
  }), [items]);

  return (
    <div className="space-y-6" data-testid="page-fornitori">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="overline">Anagrafiche</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black flex items-center gap-2">
            <Building2 className="w-7 h-7 text-slate-500" /> Agenda Fornitori
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Anagrafica raccolta automaticamente dalle bolle. Modifica indirizzo, P.IVA,
            IBAN e condizioni di pagamento — la prossima estrazione AI riutilizzerà i dati.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${REACT_APP_BACKEND_URL}/api/export/fornitori/excel`}
            target="_blank" rel="noreferrer"
            data-testid="btn-export-fornitori-excel"
            className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 hover:bg-slate-50 text-xs font-semibold rounded-sm"
            title="Esporta anagrafica + spese per il commercialista"
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-700" /> Excel commercialista
          </a>
          <button
            onClick={() => setEditing({ ...EMPTY_FORM })}
            data-testid="btn-nuovo-fornitore"
            className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold rounded-sm"
          >
            <Plus className="w-4 h-4" /> Nuovo fornitore
          </button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-200 pb-2">
        <button type="button" onClick={() => setActiveTab("rubrica")}
          className={`px-4 py-2 text-sm font-semibold rounded ${activeTab === "rubrica" ? "bg-slate-800 text-white" : "bg-white border border-slate-200"}`}>
          Rubrica fornitori
        </button>
        <button type="button" onClick={() => setActiveTab("costi")}
          className={`px-4 py-2 text-sm font-semibold rounded ${activeTab === "costi" ? "bg-slate-800 text-white" : "bg-white border border-slate-200"}`}>
          Costi e Rating
        </button>
      </div>
      {activeTab === "costi" ? (
        <FornitoriCostiRating fornitori={items} />
      ) : (
        <>
      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Totale fornitori" value={kpi.tot} tone="slate" />
        <Kpi label="In anagrafica" value={kpi.con_anagrafica} tone="emerald" />
        <Kpi label="Solo bolle (senza anagrafica)" value={kpi.orfani} tone="amber" />
        <Kpi label="Spesa complessiva" value={eur(kpi.spesa)} tone="slate-dark" />
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-3 py-2">
        <Search className="w-4 h-4 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="input-search-fornitori"
          placeholder="Cerca per ragione sociale, P.IVA o indirizzo…"
          className="flex-1 outline-none text-sm bg-transparent"
        />
        <span className="text-xs text-slate-500">{filtered.length} risultati</span>
      </div>

      {/* Tabella */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="table-fornitori">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left p-3">Ragione sociale</th>
                <th className="text-left p-3">P.IVA · Indirizzo</th>
                <th className="text-left p-3">Contatti</th>
                <th className="text-right p-3">Bolle</th>
                <th className="text-right p-3">Spesa totale</th>
                <th className="text-left p-3">Ultima bolla</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={7} className="p-6 text-center text-slate-400 italic">Caricamento…</td></tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-400 italic">
                    Nessun fornitore.<br />
                    <span className="text-[11px]">I fornitori vengono creati automaticamente quando salvi una bolla o usi l'estrazione AI.</span>
                  </td>
                </tr>
              )}
              {filtered.map((f) => (
                <tr key={f.fornitore_id || f.ragione_sociale}
                    className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-3">
                    <div className="font-semibold flex items-center gap-2">
                      {f.ragione_sociale}
                      {f.solo_bolle && (
                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-sm border border-amber-200">
                          solo bolle
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500">{f.categoria || "—"}</div>
                  </td>
                  <td className="p-3 text-xs">
                    {f.partita_iva && <div className="font-mono">P.IVA {f.partita_iva}</div>}
                    {f.indirizzo && (
                      <div className="text-slate-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" /> {f.indirizzo}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-xs text-slate-600">
                    {f.telefono && <div className="flex items-center gap-1"><Phone className="w-3 h-3" /> {f.telefono}</div>}
                    {f.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {f.email}</div>}
                    {f.iban && <div className="flex items-center gap-1 font-mono"><Landmark className="w-3 h-3" /> {f.iban}</div>}
                    {!f.telefono && !f.email && !f.iban && <span className="text-slate-300">—</span>}
                  </td>
                  <td className="p-3 text-right font-mono tabular-nums">{f.n_bolle}</td>
                  <td className="p-3 text-right font-mono tabular-nums font-bold">
                    {f.spesa_totale > 0 ? eur(f.spesa_totale) : "—"}
                  </td>
                  <td className="p-3 text-xs text-slate-500">{f.ultima_bolla || "—"}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      {f.n_bolle > 0 && f.fornitore_id && (
                        <button
                          onClick={() => setHistoryFor(f)}
                          data-testid={`btn-storico-${f.fornitore_id}`}
                          title="Storico bolle"
                          className="p-1 text-slate-700 hover:bg-slate-100 rounded-sm"
                        >
                          <Receipt className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setEditing({ ...EMPTY_FORM, ...f })}
                        data-testid={`btn-edit-${f.fornitore_id || "orphan"}`}
                        title={f.solo_bolle ? "Aggiungi anagrafica" : "Modifica"}
                        className="p-1 text-slate-700 hover:bg-slate-100 rounded-sm"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      {f.fornitore_id && (
                        <button
                          onClick={() => { if (confirm(`Eliminare "${f.ragione_sociale}"?`)) delMut.mutate(f.fornitore_id); }}
                          data-testid={`btn-del-${f.fornitore_id}`}
                          title="Elimina anagrafica (le bolle restano)"
                          className="p-1 text-rose-700 hover:bg-rose-100 rounded-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

        </>
      )}

      {editing && (
        <FormModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={(payload) => saveMut.mutateAsync(payload)}
          submitting={saveMut.isPending}
        />
      )}
      {historyFor && (
        <StoricoBolleModal
          fornitore={historyFor}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    "slate-dark": "bg-slate-800 text-white",
    emerald: "bg-emerald-100 text-emerald-800",
    amber: "bg-amber-100 text-amber-800",
  };
  return (
    <div className={`p-4 rounded-md ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-black mt-1">{value}</div>
    </div>
  );
}

function FormModal({ initial, onClose, onSave, submitting }) {
  const [form, setForm] = React.useState(initial);
  const isNew = !initial.fornitore_id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isFornitoreDirty = (f) => Boolean(
    (f.ragione_sociale && String(f.ragione_sociale).trim()) ||
    (f.partita_iva && String(f.partita_iva).trim()) ||
    (f.indirizzo && String(f.indirizzo).trim()) ||
    (f.email && String(f.email).trim()) ||
    (f.telefono && String(f.telefono).trim()) ||
    (f.iban && String(f.iban).trim()) ||
    (f.note && String(f.note).trim())
  );
  const { clearDraft, safeClose, backdropHandlers, dirty } = useFormDraft({
    storageKey: isNew ? "draft:fornitore:new" : `draft:fornitore:${initial.fornitore_id}`,
    form,
    setForm,
    isDirty: isFornitoreDirty,
    onClose,
  });
  // ESC → chiusura sicura
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") safeClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" {...backdropHandlers}>
      <div className="bg-white w-full max-w-2xl border border-slate-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onMouseUp={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="font-display font-bold text-xl">
              {isNew ? "Nuovo fornitore" : "Modifica fornitore"}
            </h2>
            {dirty && !submitting && (
              <div className="text-[11px] text-emerald-700 mt-0.5 inline-flex items-center gap-1" data-testid="fornitore-autosave-indicator">
                <Save className="w-3 h-3" /> Bozza salvata automaticamente
              </div>
            )}
          </div>
          <button onClick={safeClose} data-testid="btn-close-fornitore-modal"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-5 grid md:grid-cols-2 gap-4 text-sm">
          <Field label="Ragione sociale" value={form.ragione_sociale} onChange={(v) => set("ragione_sociale", v)} testid="fornitore-ragione" />
          <Field label="Partita IVA" value={form.partita_iva} onChange={(v) => set("partita_iva", v)} testid="fornitore-piva" mono />
          <div className="md:col-span-2">
            <Field label="Indirizzo" value={form.indirizzo} onChange={(v) => set("indirizzo", v)} testid="fornitore-indirizzo" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Categoria</label>
            <select value={form.categoria} onChange={(e) => set("categoria", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">
              {CATEGORIE.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <Field label="Telefono" value={form.telefono} onChange={(v) => set("telefono", v)} />
          <Field label="Email" value={form.email} onChange={(v) => set("email", v)} type="email" />
          <Field label="Condizioni pagamento" value={form.condizioni_pagamento} onChange={(v) => set("condizioni_pagamento", v)} />
          <div className="md:col-span-2">
            <Field label="IBAN" value={form.iban} onChange={(v) => set("iban", v)} mono />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-slate-600 block mb-1">Note</label>
            <textarea rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={safeClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50">Annulla</button>
          <button
            onClick={async () => {
              const { fornitore_id, n_bolle, imponibile_totale, spesa_totale, ultima_bolla, solo_bolle, created_at, ...clean } = form;
              try {
                await onSave(clean);
                clearDraft();
              } catch (_) {
                // errore già segnalato dal parent — bozza mantenuta
              }
            }}
            disabled={submitting}
            data-testid="btn-salva-fornitore"
            className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-white font-semibold rounded-sm"
          >
            {submitting ? "Salvo…" : (isNew ? "Crea fornitore" : "Salva modifiche")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", mono = false, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 block mb-1">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className={`w-full px-3 py-2 border border-slate-300 rounded-sm text-sm ${mono ? "font-mono" : ""}`}
      />
    </div>
  );
}

function StoricoBolleModal({ fornitore, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["fornitore-bolle", fornitore.fornitore_id],
    queryFn: () => api.get(`/entita/fornitori/${fornitore.fornitore_id}/bolle`).then((r) => r.data),
  });
  const bolle = data?.bolle || [];
  const tot = bolle.reduce((s, b) => s + Number(b.totale_con_iva || 0), 0);

  // Aggregazione mese-per-mese per il grafico (ultimi 12 mesi rispetto ad oggi)
  const chartData = React.useMemo(() => {
    const now = new Date();
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("it-IT", { month: "short", year: "2-digit" });
      months.push({ key, label, spesa: 0, count: 0 });
    }
    for (const b of bolle) {
      const k = (b.data || "").slice(0, 7);
      const m = months.find((x) => x.key === k);
      if (m) {
        m.spesa += Number(b.totale_con_iva || 0);
        m.count += 1;
      }
    }
    return months.map((m) => ({ ...m, spesa: Math.round(m.spesa * 100) / 100 }));
  }, [bolle]);

  const trend = React.useMemo(() => {
    // Confronto media ultimi 3 mesi vs 3 precedenti
    const last3 = chartData.slice(-3).reduce((s, m) => s + m.spesa, 0);
    const prev3 = chartData.slice(-6, -3).reduce((s, m) => s + m.spesa, 0);
    if (prev3 === 0 && last3 === 0) return null;
    if (prev3 === 0) return { delta: 100, direction: "up" };
    const delta = ((last3 - prev3) / prev3) * 100;
    return { delta: Math.round(delta), direction: delta >= 0 ? "up" : "down" };
  }, [chartData]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl border border-slate-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="overline">Storico bolle</div>
            <h2 className="font-display font-bold text-xl flex items-center gap-2">
              <Building2 className="w-5 h-5" /> {fornitore.ragione_sociale}
            </h2>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-5">
          {isLoading && <div className="text-center text-slate-400 py-8">Caricamento…</div>}
          {!isLoading && bolle.length === 0 && (
            <div className="text-center text-slate-400 py-8 italic">Nessuna bolla registrata.</div>
          )}
          {bolle.length > 0 && (
            <>
              <div className="flex items-center gap-3 mb-3 text-sm flex-wrap">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-sm">
                  <TrendingUp className="w-4 h-4 text-emerald-700" />
                  <b>{bolle.length}</b> bolle · <b>{eur(tot)}</b> totale con IVA
                </div>
                {trend && (
                  <div
                    data-testid="fornitore-trend-badge"
                    className={`flex items-center gap-2 px-3 py-1.5 border rounded-sm ${trend.direction === "up"
                      ? "bg-rose-50 border-rose-200 text-rose-700"
                      : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}
                  >
                    <TrendingUp className={`w-4 h-4 ${trend.direction === "down" ? "rotate-180" : ""}`} />
                    Trend ultimi 3 mesi vs 3 precedenti: <b>{trend.direction === "up" ? "+" : ""}{trend.delta}%</b>
                  </div>
                )}
              </div>

              {/* Mini grafico spese mese per mese (ultimi 12 mesi) */}
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mb-4"
                   data-testid="fornitore-chart-spese">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
                  Spese mese per mese (ultimi 12 mesi)
                </div>
                <div className="h-40 w-full">
                  <ResponsiveContainer>
                    <BarChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8"
                             tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} />
                      <Tooltip
                        formatter={(v) => [eur(v), "Spesa"]}
                        labelFormatter={(l) => `Mese: ${l}`}
                        contentStyle={{ fontSize: 11 }}
                      />
                      <Bar dataKey="spesa" fill="#1e293b" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="text-left p-2">Data</th>
                    <th className="text-left p-2">N° Bolla</th>
                    <th className="text-left p-2">Cantiere</th>
                    <th className="text-left p-2">Materiale</th>
                    <th className="text-right p-2">Q.tà</th>
                    <th className="text-right p-2">Imponibile</th>
                    <th className="text-right p-2">Totale IVA</th>
                  </tr>
                </thead>
                <tbody>
                  {bolle.map((b) => (
                    <tr key={b.bolla_id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="p-2">{b.data}</td>
                      <td className="p-2 font-mono">{b.numero_bolla}</td>
                      <td className="p-2">{b.cantiere_nome || "—"}</td>
                      <td className="p-2">{b.materiale}</td>
                      <td className="p-2 text-right">{b.quantita} {b.unita_misura}</td>
                      <td className="p-2 text-right font-mono">{eur(b.imponibile)}</td>
                      <td className="p-2 text-right font-mono font-bold">{eur(b.totale_con_iva)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
